import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  Search,
  Book,
  ChevronRight,
  ChevronLeft,
  Loader2,
  HelpCircle,
  PlayCircle,
  Smartphone,
  Clock,
  FileText,
  MessageSquare,
  ClipboardList
} from 'lucide-react';

// Icon mapping
const ICON_MAP = {
  PlayCircle,
  Smartphone,
  Clock,
  FileText,
  MessageSquare,
  ClipboardList,
  HelpCircle,
  Book
};

const MobileHelp = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  
  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Fetch help content
  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/mobile/help/content');
      
      if (response.data.from_database) {
        setCategories(response.data.categories);
        setArticles(response.data.articles);
      }
    } catch (error) {
      console.error('Failed to fetch help content:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Get current category data
  const currentCategory = selectedCategory 
    ? categories.find(c => c.key === selectedCategory) 
    : null;
  
  // Get articles for current category
  const currentArticles = selectedCategory
    ? articles.filter(a => a.category_key === selectedCategory)
    : [];

  // Filter by search
  const filteredCategories = searchQuery.trim()
    ? categories.filter(cat => {
        const categoryArticles = articles.filter(a => a.category_key === cat.key);
        return categoryArticles.some(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.content.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
    : categories;

  const filteredArticles = searchQuery.trim()
    ? articles.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentArticles;

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    let currentListType = null;

    const flushList = () => {
      if (listItems.length > 0) {
        if (currentListType === 'ordered') {
          elements.push(
            <ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-1 mb-3 ml-1 text-gray-300">
              {listItems}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-1 mb-3 ml-1 text-gray-300">
              {listItems}
            </ul>
          );
        }
        listItems = [];
        currentListType = null;
      }
    };

    const parseInlineBold = (text) => {
      if (!text.includes('**')) return text;
      const parts = text.split(/\*\*(.+?)\*\*/g);
      return parts.map((part, j) => 
        j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part
      );
    };

    lines.forEach((line, i) => {
      const numberedMatch = line.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        if (currentListType !== 'ordered') {
          flushList();
          currentListType = 'ordered';
        }
        listItems.push(<li key={`li-${i}`}>{parseInlineBold(numberedMatch[2])}</li>);
        return;
      }

      if (line.startsWith('- ') || line.startsWith('• ')) {
        if (currentListType !== 'unordered') {
          flushList();
          currentListType = 'unordered';
        }
        listItems.push(<li key={`li-${i}`}>{parseInlineBold(line.substring(2))}</li>);
        return;
      }

      flushList();

      if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <h4 key={i} className="font-semibold mt-4 mb-2 text-white">{line.replace(/\*\*/g, '')}</h4>
        );
        return;
      }

      if (line.includes('**')) {
        elements.push(
          <p key={i} className="mb-2 text-gray-300">{parseInlineBold(line)}</p>
        );
        return;
      }

      if (!line.trim()) {
        elements.push(<div key={i} className="h-2" />);
        return;
      }

      elements.push(<p key={i} className="mb-2 text-gray-300">{line}</p>);
    });

    flushList();
    return elements;
  };

  // Navigation handlers
  const handleCategoryClick = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else {
      navigate('/mobile/dashboard');
    }
  };

  // Get icon for category
  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Book;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] border-b border-gray-700 sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="text-gray-400 hover:text-white -ml-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-white font-semibold">
                {selectedArticle 
                  ? selectedArticle.title 
                  : selectedCategory 
                    ? currentCategory?.title 
                    : 'Help Center'}
              </h1>
              {!selectedArticle && !selectedCategory && (
                <p className="text-gray-400 text-xs">Guides & tutorials</p>
              )}
            </div>
          </div>
          <Book className="h-5 w-5 text-[#ffd108]" />
        </div>
        
        {/* Search - only show on categories view */}
        {!selectedCategory && !selectedArticle && (
          <div className="px-4 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Categories View */}
        {!selectedCategory && !selectedArticle && (
          <div className="space-y-3">
            {searchQuery.trim() ? (
              // Search results
              <>
                <p className="text-gray-400 text-sm mb-4">
                  {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''} found
                </p>
                {filteredArticles.map((article) => {
                  const category = categories.find(c => c.key === article.category_key);
                  return (
                    <Card 
                      key={article.id}
                      className="bg-[#2a2a2a] border-gray-700 active:bg-[#3a3a3a]"
                      onClick={() => {
                        setSelectedCategory(article.category_key);
                        setSelectedArticle(article);
                        setSearchQuery('');
                      }}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-[#ffd108]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{article.title}</p>
                            <p className="text-gray-500 text-xs">{category?.title}</p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-600" />
                      </CardContent>
                    </Card>
                  );
                })}
                {filteredArticles.length === 0 && (
                  <div className="text-center py-12">
                    <HelpCircle className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400">No articles found</p>
                  </div>
                )}
              </>
            ) : (
              // Categories list
              filteredCategories.map((category) => {
                const Icon = getIcon(category.icon);
                const articleCount = articles.filter(a => a.category_key === category.key).length;
                return (
                  <Card 
                    key={category.key}
                    className="bg-[#2a2a2a] border-gray-700 active:bg-[#3a3a3a]"
                    onClick={() => handleCategoryClick(category.key)}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-[#ffd108]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{category.title}</p>
                          <p className="text-gray-500 text-xs">
                            {articleCount} article{articleCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Articles List View */}
        {selectedCategory && !selectedArticle && (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm mb-2">{currentCategory?.description}</p>
            {currentArticles.map((article) => (
              <Card 
                key={article.id}
                className="bg-[#2a2a2a] border-gray-700 active:bg-[#3a3a3a]"
                onClick={() => handleArticleClick(article)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#ffd108] flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">{article.title}</p>
                      {article.badge && (
                        <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs mt-1">
                          {article.badge}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Article Content View */}
        {selectedArticle && (
          <div className="pb-8">
            {selectedArticle.badge && (
              <Badge className="bg-[#ffd108]/20 text-[#ffd108] mb-4">
                {selectedArticle.badge}
              </Badge>
            )}
            <div className="text-sm leading-relaxed">
              {renderContent(selectedArticle.content)}
            </div>
            
            {/* Related articles */}
            {currentArticles.length > 1 && (
              <div className="mt-8 pt-6 border-t border-gray-700">
                <h3 className="text-white font-semibold mb-3">Related Articles</h3>
                <div className="space-y-2">
                  {currentArticles
                    .filter(a => a.id !== selectedArticle.id)
                    .slice(0, 3)
                    .map((article) => (
                      <button
                        key={article.id}
                        onClick={() => handleArticleClick(article)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#2a2a2a] text-left active:bg-[#3a3a3a]"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#ffd108]" />
                        <span className="text-gray-300 text-sm">{article.title}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileHelp;
